import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ClientAuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClientAuthProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('client_token'));

  // Vérifier le token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      const savedToken = localStorage.getItem('client_token');
      if (savedToken) {
        try {
          const response = await axios.get(
            `${API_URL}/api/client-auth/verify?token=${savedToken}`
          );
          if (response.data.valid) {
            setClient(response.data.client);
            setToken(savedToken);
          } else {
            localStorage.removeItem('client_token');
            setToken(null);
          }
        } catch (error) {
          console.error('Token invalide:', error);
          localStorage.removeItem('client_token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/client-auth/login`, {
        email,
        password
      });

      const { access_token, client: clientData } = response.data;
      
      localStorage.setItem('client_token', access_token);
      setToken(access_token);
      setClient(clientData);

      return { success: true };
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Erreur de connexion'
      };
    }
  };

  const createPassword = async (email, password, confirmPassword) => {
    try {
      const response = await axios.post(`${API_URL}/api/client-auth/create-password`, {
        email,
        password,
        confirm_password: confirmPassword
      });

      const { access_token, client: clientData } = response.data;
      
      localStorage.setItem('client_token', access_token);
      setToken(access_token);
      setClient(clientData);

      return { success: true };
    } catch (error) {
      console.error('Erreur création mot de passe:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Erreur de création du mot de passe'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('client_token');
    setToken(null);
    setClient(null);
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API_URL}/api/client-auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      console.error('Erreur forgot password:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Erreur lors de la demande'
      };
    }
  };

  const resetPassword = async (token, newPassword, confirmPassword) => {
    try {
      await axios.post(`${API_URL}/api/client-auth/reset-password`, {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return { success: true };
    } catch (error) {
      console.error('Erreur reset password:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Erreur lors de la réinitialisation'
      };
    }
  };

  const value = {
    client,
    token,
    loading,
    isAuthenticated: !!client,
    login,
    logout,
    createPassword,
    forgotPassword,
    resetPassword
  };

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
};

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth doit être utilisé dans un ClientAuthProvider');
  }
  return context;
};

export default ClientAuthContext;
